import { DateTime } from "luxon";
import {
  RowStamp,
  fetchEstimateByIdUncached,
  fetchGCGRsByProjectIdUncached,
  fetchRowStampUncached,
  fetchUnitTypesByEstimateIdUncached,
} from "../../api";
import { EstimateData } from "../../interfaces/EstimateData";
import { GCGR } from "../../interfaces/GCGRs";
import { excelDateToEpochMs } from "../workbook";
import Logger from "../../clients/sentry";
import { serializeErrorForWarning } from "../../errors";

export type ReportStaleCheck = "rooms" | "units" | "area" | "alternates" | "gcgrNonstaff";

interface EstimateServerState {
  lastUpdatedAt: Date;
  statsProcessedAt: number;
}

interface ContractingServerState {
  lastUpdatedAt: Date;
  bidGroupCount: number;
  markupCount: number;
}

interface ReportCheckTarget {
  projectId: string;
  estimateId?: string;
  gcgrId?: string;
}

const BASELINE_COLUMNS: Record<"statsProcessed" | "lineItems" | ReportStaleCheck, string> = {
  statsProcessed: "stats_processed",
  lineItems: "line_items_fingerprint",
  rooms: "rooms_fingerprint",
  units: "units_fingerprint",
  area: "area_fingerprint",
  alternates: "alternates_fingerprint",
  gcgrNonstaff: "nonstaff_fingerprint",
};

const ROOM_TABLES = ["rooms", "room_groups", "keys", "fixtures"] as const;
const AREA_TABLES = ["area_matrix_items", "floor_groups", "estimate_use_group_allocation_presets"] as const;
const GCGR_NONSTAFF_TABLES = [
  "gcgr_nonstaff_details",
  "gcgr_nonstaff_activities",
  "gcgr_nonstaff_detail_crews",
] as const;

// TODO: Remove each report check once the API bumps its datasource's updated_at on these edits:
// rooms, room groups, keys and fixtures; unit types and their spec associations; area-matrix items,
// floor groups (buildings) and allocation presets; alternates (VM decisions are columns on them); and
// GCGR non-staff details, activities and crews. Their rows are hard-deleted, so a delete only shows
// in the count.
const REPORT_CHECKS: Record<
  ReportStaleCheck,
  { datasource: "estimate" | "gcgr"; probe: (target: ReportCheckTarget) => Promise<string> }
> = {
  rooms: { datasource: "estimate", probe: (target) => probeEstimateTables(target.estimateId ?? "", ROOM_TABLES) },
  units: { datasource: "estimate", probe: (target) => probeUnits(target.estimateId ?? "") },
  area: { datasource: "estimate", probe: (target) => probeEstimateTables(target.estimateId ?? "", AREA_TABLES) },
  alternates: {
    datasource: "estimate",
    probe: (target) => probeEstimateTables(target.estimateId ?? "", ["alternates"]),
  },
  gcgrNonstaff: {
    datasource: "gcgr",
    probe: (target) => probeGcgrTables(target.projectId, target.gcgrId ?? "", GCGR_NONSTAFF_TABLES),
  },
};

/**
 * Whether the server changed since a report row was written: compares the server's updated_at with
 * the one the row saved, both from the server's clock so the user's can't skew it. A row without
 * one counts as changed, so its first refresh writes the baseline.
 */
export const isNewerThanRow = (serverChange: Date, row?: { updated_at?: unknown }): boolean => {
  const savedServerStamp = excelDateToEpochMs(row?.updated_at);
  if (Number.isNaN(savedServerStamp)) return true;
  // A date column keeps whole seconds only, so compare at that precision
  return Math.floor(serverChange.getTime() / 1000) > Math.floor(savedServerStamp / 1000);
};

// Newest server updated_at across the given rows as an ISO string ("" if none). Populates the
// contracting report's `updated_at`, the baseline the change check compares the server's with.
export const maxServerUpdatedAt = (rows: Array<{ updated_at?: string | null }>): string => {
  const times = rows.map((row) => new Date(row.updated_at ?? "").getTime()).filter((time) => !Number.isNaN(time));
  return times.length ? new Date(Math.max(...times)).toISOString() : "";
};

const wholeSeconds = (stamp: unknown): number | undefined => {
  if (typeof stamp !== "string" || !stamp) return undefined;
  const parsed = DateTime.fromISO(stamp.replace(" ", "T"));
  return parsed.isValid ? Math.floor(parsed.toSeconds()) : undefined;
};

const fingerprint = (count: number, stamps: unknown[]): string => {
  const seconds = stamps.map(wholeSeconds).filter((value): value is number => value !== undefined);
  return `${count}@${seconds.length > 0 ? Math.max(...seconds) : ""}`;
};

const rowsFingerprint = (rows: unknown[] | null | undefined): string =>
  fingerprint(
    rows?.length ?? 0,
    (rows ?? []).map((row) => (row as { updated_at?: unknown } | null)?.updated_at)
  );

const stampFingerprint = ({ count, newestUpdatedAt }: RowStamp): string => fingerprint(count, [newestUpdatedAt]);

const getEstimateServerState = async (estimateId: string): Promise<EstimateServerState> => {
  const estimateResult = await fetchEstimateByIdUncached(estimateId);
  if (!estimateResult.success) {
    throw estimateResult.error;
  }
  const estimate = estimateResult.data;
  return {
    lastUpdatedAt: new Date(estimate?.updated_at || "3000-01-01T00:00:00Z"),
    statsProcessedAt: wholeSeconds(estimate?.stats?.processed) ?? 0,
  };
};

const getGCGRLastUpdatedAt = async (projectId: string, gcgrId: string): Promise<Date> => {
  // Uncached, like the estimate and contracting probes: a memoized list would freeze updated_at at
  // whatever the first caller saw, so the probe could never report a change. Only the selected GCGR
  // is probed; edits to other GCGRs in __GCGR don't trigger a refresh, since no report reads them.
  // Unfiltered: an inactive GCGR is selectable, and probing an active-only list would report the
  // selection as missing on every check — permanently stale, with a refresh that can never converge.
  const gcgrsResult = await fetchGCGRsByProjectIdUncached(projectId, { activeOnly: false });
  if (!gcgrsResult.success) {
    throw gcgrsResult.error;
  }
  const gcgr = (gcgrsResult.data ?? []).find((g) => g.id === gcgrId);
  // Deleted or moved off the project — report it as changed so the refresh path clears it
  if (!gcgr) return new Date("3000-01-01T00:00:00Z");
  return new Date(gcgr.updated_at || "3000-01-01T00:00:00Z");
};

const getContractingServerState = async (projectId: string): Promise<ContractingServerState> => {
  // Markup edits live in internal_estimate_markups and don't bump bid_groups.updated_at,
  // so probe both tables and take the newest change. Scope-sheet internals (alternate bids,
  // trade cleanup bids) still have no cheap change signal — a contracting report refresh
  // compensates by always refetching scope data.
  const [bidGroupsResult, markupsResult] = await Promise.all([
    fetchRowStampUncached("bid_groups", ["qual;NULL", "AND", "frontend;NULL", "AND", `project;${projectId}`], {
      params: { projectId },
    }),
    fetchRowStampUncached("internal_estimate_markups", `internal_estimate_markups.project;${projectId}`),
  ]);
  if (!bidGroupsResult.success) {
    throw bidGroupsResult.error;
  }
  if (!markupsResult.success) {
    throw markupsResult.error;
  }
  const bidGroups = bidGroupsResult.data ?? { count: 0, newestUpdatedAt: undefined };
  const markups = markupsResult.data ?? { count: 0, newestUpdatedAt: undefined };
  const counts = { bidGroupCount: bidGroups.count, markupCount: markups.count };
  if (bidGroups.count === 0) return { lastUpdatedAt: new Date("3000-01-01T00:00:00Z"), ...counts };
  const timestamps = [bidGroups.newestUpdatedAt, markups.newestUpdatedAt]
    .map((stamp) => new Date(stamp ?? "").getTime())
    .filter((time) => !Number.isNaN(time));
  // All rows unparseable → Math.max() would be -Infinity (Invalid Date, never stale);
  // treat as changed so the refresh path re-evaluates
  if (timestamps.length === 0) return { lastUpdatedAt: new Date("3000-01-01T00:00:00Z"), ...counts };
  return { lastUpdatedAt: new Date(Math.max(...timestamps)), ...counts };
};

// TODO: Remove once deleting a bid group or an internal markup bumps a stamp the check reads. Both
// are hard-deleted, so a delete never moves the newest updated_at; only the counts show it.
const contractingRowCountsChanged = (
  state: ContractingServerState,
  row?: { bid_group_count?: unknown; markup_count?: unknown }
): boolean =>
  Number(row?.bid_group_count ?? NaN) !== state.bidGroupCount || Number(row?.markup_count ?? NaN) !== state.markupCount;

export const estimateStaleCheckBaselines = (data: EstimateData): Record<string, string> => ({
  [BASELINE_COLUMNS.statsProcessed]: String(wholeSeconds(data.estimate?.stats?.processed) ?? 0),
  [BASELINE_COLUMNS.lineItems]: rowsFingerprint((data.line_items ?? []).filter((lineItem) => !lineItem.deleted_at)),
  [BASELINE_COLUMNS.rooms]: [data.rooms, data.room_groups, data.keys, data.fixtures].map(rowsFingerprint).join("|"),
  [BASELINE_COLUMNS.units]: [data.unit_types, data.unit_type_unit_spec_associations].map(rowsFingerprint).join("|"),
  [BASELINE_COLUMNS.area]: [data.area_matrix_items, data.floor_groups, data.estimate_use_group_allocation_presets]
    .map(rowsFingerprint)
    .join("|"),
  [BASELINE_COLUMNS.alternates]: rowsFingerprint(data.alternates),
});

export const gcgrStaleCheckBaselines = (gcgr: GCGR): Record<string, string> => ({
  [BASELINE_COLUMNS.gcgrNonstaff]: [
    gcgr.gcgr_nonstaff_details,
    gcgr.gcgr_nonstaff_activities,
    gcgr.gcgr_nonstaff_detail_crews,
  ]
    .map(rowsFingerprint)
    .join("|"),
});

const fetchStampFingerprint = async (table: string, search: string, projectId?: string): Promise<string> => {
  const result = await fetchRowStampUncached(table, search, { projectId });
  if (!result.success) throw result.error;
  return stampFingerprint(result.data ?? { count: 0, newestUpdatedAt: undefined });
};

const probeEstimateTables = async (estimateId: string, tables: readonly string[]): Promise<string> => {
  const stamps = await Promise.all(
    tables.map((table) => fetchStampFingerprint(table, `${table}.estimate;${estimateId}`))
  );
  return stamps.join("|");
};

const probeUnits = async (estimateId: string): Promise<string> => {
  const unitTypesResult = await fetchUnitTypesByEstimateIdUncached(estimateId);
  if (!unitTypesResult.success) throw unitTypesResult.error;
  const unitTypes = unitTypesResult.data ?? [];
  const associations =
    unitTypes.length > 0
      ? await fetchStampFingerprint(
          "unit_type_unit_spec_associations",
          `unit_type_unit_spec_associations.unit_type;IN;${unitTypes.map((unitType) => unitType.id).join(",")}`
        )
      : rowsFingerprint([]);
  return [rowsFingerprint(unitTypes), associations].join("|");
};

const probeGcgrTables = async (projectId: string, gcgrId: string, tables: readonly string[]): Promise<string> => {
  const stamps = await Promise.all(tables.map((table) => fetchStampFingerprint(table, `gcgr;${gcgrId}`, projectId)));
  return stamps.join("|");
};

const differsFromRow = (current: string, column: string, row?: Record<string, unknown>): boolean =>
  current !== row?.[column];

const warnAndKeepStamp = (label: string) => (error: unknown) => {
  Logger.warn(`${label} failed, using the stamp alone`, serializeErrorForWarning(error));
  return false;
};

// TODO: Remove the line-item part once the API bumps the estimate's updated_at on line items written
// by an import (Submit to Estimate), apply-to-similar or UPC links; they're hard-deleted, so a delete
// only shows in the count. The stats are recomputed by a worker after updated_at moves, so a report
// refreshed in between gets the old totals; stats.processed moving lets the next refresh pick them up.
export const estimateDefaultChanged = async (estimateId: string, row?: Record<string, unknown>): Promise<boolean> => {
  const [state, lineItemsChanged] = await Promise.all([
    getEstimateServerState(estimateId),
    probeEstimateTables(estimateId, ["line_items"])
      .then((lineItems) => differsFromRow(lineItems, BASELINE_COLUMNS.lineItems, row))
      .catch(warnAndKeepStamp("Estimate line-item check")),
  ]);
  return (
    isNewerThanRow(state.lastUpdatedAt, row) ||
    Number(row?.[BASELINE_COLUMNS.statsProcessed] ?? NaN) !== state.statsProcessedAt ||
    lineItemsChanged
  );
};

export const gcgrDefaultChanged = async (
  projectId: string,
  gcgrId: string,
  row?: Record<string, unknown>
): Promise<boolean> => isNewerThanRow(await getGCGRLastUpdatedAt(projectId, gcgrId), row);

export const contractingDefaultChanged = async (
  projectId: string,
  row?: { updated_at?: unknown; bid_group_count?: unknown; markup_count?: unknown }
): Promise<boolean> => {
  const state = await getContractingServerState(projectId);
  return isNewerThanRow(state.lastUpdatedAt, row) || contractingRowCountsChanged(state, row);
};

export const reportChecksChanged = async (
  datasource: "estimate" | "gcgr",
  checks: ReportStaleCheck[],
  target: ReportCheckTarget,
  row?: Record<string, unknown>
): Promise<boolean> => {
  const results = await Promise.all(
    checks
      .filter((check) => REPORT_CHECKS[check].datasource === datasource)
      .map((check) =>
        REPORT_CHECKS[check]
          .probe(target)
          .then((current) => differsFromRow(current, BASELINE_COLUMNS[check], row))
          .catch(warnAndKeepStamp(`The ${check} check`))
      )
  );
  return results.some(Boolean);
};
